import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DealerRecommendation } from '@quorum/models/xs-misc';
import { ServiceGroup } from '@quorum/models/xs-resource';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

export interface DealerRecommendationEditDialogData {
  mode: 'create' | 'edit';
  recommendation: DealerRecommendation | null;
  serviceGroups: ServiceGroup[];
  rooftopId: number;
}

export type DealerRecommendationEditDialogResult =
  | { action: 'save'; payload: { serviceGroupId: string; intervalMileage?: number; intervalMonths?: number } }
  | null;

export const atLeastOneIntervalValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const mileage = group.get('intervalMileage')?.value;
  const months = group.get('intervalMonths')?.value;
  const hasMileage = typeof mileage === 'number' && mileage > 0;
  const hasMonths = typeof months === 'number' && months > 0;
  return hasMileage || hasMonths ? null : { noInterval: true };
};

@Component({
  selector: 'svc-dealer-recommendation-edit-dialog',
  templateUrl: './dealer-recommendation-edit-dialog.component.html',
  styleUrls: ['./dealer-recommendation-edit-dialog.component.scss'],
})
export class DealerRecommendationEditDialogComponent implements OnInit, OnDestroy {
  form = new FormGroup(
    {
      serviceGroupId: new FormControl(null, Validators.required),
      intervalMileage: new FormControl(null, [Validators.min(1)]),
      intervalMonths: new FormControl(null, [Validators.min(1)]),
    },
    { validators: atLeastOneIntervalValidator }
  );

  searchControl = new FormControl('');

  filteredGroups: ServiceGroup[] = [];
  selectedGroup: ServiceGroup | null = null;
  distanceUnitLabel: 'mi' | 'km' = 'mi';

  private destroy$ = new Subject<void>();

  get canSave(): boolean {
    return this.form.valid;
  }

  get isEditMode(): boolean {
    return this.data.mode === 'edit';
  }

  get title(): string {
    return this.isEditMode ? 'Edit Recommendation' : 'Add Recommendation';
  }

  constructor(
    public dialogRef: MatDialogRef<DealerRecommendationEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DealerRecommendationEditDialogData,
    private systemControlStateService: SystemControlStateService
  ) {}

  ngOnInit(): void {
    this.recomputeFilteredGroups('');

    const rec = this.data.recommendation;
    if (rec) {
      const sg = this.data.serviceGroups.find((g) => g.id === rec.serviceGroupId) ?? null;
      if (sg) {
        this.selectedGroup = sg;
        this.form.controls.serviceGroupId.setValue(sg.id);
      }
      this.form.controls.intervalMileage.setValue(rec.intervalMileage ?? null);
      this.form.controls.intervalMonths.setValue(rec.intervalMonths ?? null);
    }

    this.systemControlStateService
      .getSystemControlValues(['CL_UNIT_OF_MEASURE'], this.data.rooftopId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((values) => {
        this.distanceUnitLabel = values?.CL_UNIT_OF_MEASURE?.value === 'M' ? 'mi' : 'km';
      });

    this.searchControl.valueChanges
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((search) => this.recomputeFilteredGroups(search ?? ''));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private recomputeFilteredGroups(search: string): void {
    const term = search.toLowerCase().trim();
    const base = this.data.serviceGroups
      .filter((sg) => sg.rooftopId === this.data.rooftopId || sg.rooftopId === -1)
      .filter(
        (sg) =>
          !term ||
          (sg.workOrderLocationOpCode ?? '').toLowerCase().includes(term) ||
          (sg.name ?? '').toLowerCase().includes(term)
      );
    this.filteredGroups = base.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  selectGroup(group: ServiceGroup): void {
    this.selectedGroup = group;
    this.form.controls.serviceGroupId.setValue(group.id);
    this.form.controls.serviceGroupId.markAsTouched();
  }

  isSelected(group: ServiceGroup): boolean {
    return !!this.selectedGroup && this.selectedGroup.id === group.id;
  }

  save(): void {
    if (!this.form.valid || !this.selectedGroup) return;

    const mileage = this.form.controls.intervalMileage.value;
    const months = this.form.controls.intervalMonths.value;

    const payload: { serviceGroupId: string; intervalMileage?: number; intervalMonths?: number } = {
      serviceGroupId: this.selectedGroup.id,
    };
    if (typeof mileage === 'number' && mileage > 0) payload.intervalMileage = mileage;
    if (typeof months === 'number' && months > 0) payload.intervalMonths = months;

    this.dialogRef.close({ action: 'save', payload } as DealerRecommendationEditDialogResult);
  }

  trackById(_index: number, item: ServiceGroup): string {
    return item.id;
  }
}
